import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useContext } from 'use-context-selector';
import { cloneDeep } from 'lodash';
import {
  PerkWaveStats,
  Results as ResultsType,
  simulate,
  Stats,
  WaveStats,
} from 'utils/simulate';
import './simulator.scss';
import { useInputState } from 'utils/hooks';
import { GameState } from 'contexts/gameState';
import { getInitialResults, Results } from 'contexts/results';
import Averages from './Averages';
import ResultsTable from './ResultsTable';
import CsvTables from './CsvTables';
import Graphs from './Graphs';
import Log from './Log';

enum Views {
  AVERAGES = 'AVERAGES',
  TABLE = 'TABLE',
  GRAPHS = 'GRAPHS',
  LOG = 'LOG',
  CSV = 'CSV',
}

// TODO - refactor shared logic
// Modifies newResults by adding in the keys. Intentionally mutates to save cloning an object per sim; just one cloneDeep beforehand.
const addResults = (newResults: ResultsType, stats: Stats) => {
  const { count } = newResults;
  const resultStats = newResults.stats;
  Object.keys(resultStats.finalStats).forEach(stat => {
    resultStats.finalStats[stat] =
      ((resultStats.finalStats[stat] || 0) * count +
        (stats.finalStats[stat] || 0)) /
      (count + 1);
  });
  Object.keys(stats.perkWaveStats).forEach(stat => {
    const waveStats: PerkWaveStats = stats.perkWaveStats[stat];
    if (waveStats.has) {
      const newWaveStats: PerkWaveStats = newResults.stats.perkWaveStats[stat];
      newWaveStats.has += waveStats.has;
      newWaveStats.wave += waveStats.wave;
    }
  });
  Object.keys(stats.waves).forEach(wave => {
    const statsWave: WaveStats = stats.waves[wave];
    if (!resultStats.waves[wave]) resultStats.waves[wave] = {};
    const resultWave: WaveStats = resultStats.waves[wave];
    Object.keys(statsWave).forEach(stat => {
      resultWave[stat] =
        ((resultWave[stat] || 0) * count + (statsWave[stat] || 0)) /
        (count + 1);
    });
  });
  // eslint-disable-next-line no-param-reassign
  newResults.count += 1;
  return newResults;
};

const mergeResults = (oldResults: ResultsType, results: ResultsType) => {
  const oldResultsCount = oldResults.count;
  const resultsCount = results.count;
  const resultsStats = results.stats;
  const newResults: ResultsType = {
    ...oldResults,
    stats: {
      ...oldResults.stats,
      waves: { ...oldResults.stats.waves },
    },
  };
  const newResultStats = newResults.stats;
  Object.keys(resultsStats.finalStats).forEach(stat => {
    newResultStats.finalStats[stat] =
      (newResultStats.finalStats[stat] * oldResultsCount +
        (resultsStats.finalStats[stat] || 0) * resultsCount) /
      (oldResultsCount + resultsCount);
  });
  Object.keys(resultsStats.perkWaveStats).forEach(stat => {
    const waveStats: PerkWaveStats = resultsStats.perkWaveStats[stat];
    if (waveStats.has) {
      newResults.stats.perkWaveStats[stat] = {
        ...oldResults.stats.perkWaveStats[stat],
      };
      const newWaveStats: PerkWaveStats = newResults.stats.perkWaveStats[stat];
      newWaveStats.has += waveStats.has;
      newWaveStats.wave += waveStats.wave;
    }
  });
  const newWaves = newResultStats.waves;
  const resultWaves = resultsStats.waves;
  Object.keys(resultWaves).forEach(wave => {
    const resultWave: WaveStats = resultWaves[wave];
    if (!newWaves[wave]) newWaves[wave] = {};
    const newWave: WaveStats = newWaves[wave];
    Object.keys(resultWave).forEach(stat => {
      newWave[stat] =
        ((newWave[stat] || 0) * oldResultsCount +
          (resultWave[stat] || 0) * resultsCount) /
        (oldResultsCount + resultsCount);
    });
  });
  newResults.count += results.count;
  return newResults;
};

const Options: React.FC<{}> = () => {
  const {
    simulationCount,
    maxWave,
    smartMissilesUnlocked,
    deathWaveUnlocked,
    chainLightningUnlocked,
    chronoFieldUnlocked,
    goldenTowerUnlocked,
    innerLandMinesUnlocked,
    poisonSwampUnlocked,
    blackHoleUnlocked,
    spotlightUnlocked,
    standardResearch,
    tradeoffResearch,
    waveReductionResearch,
    perkOptionResearch,
    coinBonuses,
    coinsPerKill,
    coinsPerWave,
    goldenTowerBonus,
    goldenTowerDuration,
    goldenTowerCooldown,
    spotlightCoins,
    spotlightAngle,
    spotlightQuantity,
    blackHoleCoverage,
    blackHoleCoins,
    blackHoleDuration,
    blackHoleCooldown,
    criticalCoin,
    firstPerkChoice,
    bannedPerks,
    perkPriorityList,
    neverPickPerks,
    gameSpeed,
    waveSkip,
    introSprint,
    autopick,
    waveAccelerator,
  } = useContext(GameState);
  const { setResults, setLog } = useContext(Results);

  const [view, setView] = useInputState(Views.TABLE, 'simulatorView');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const cancelRef = useRef<number>();

  const clear = useCallback(() => {
    setLog([]);
    setResults(getInitialResults());
  }, [setLog, setResults]);

  const simulation = useCallback(
    verbose =>
      simulate({
        smartMissilesUnlocked,
        maxWave,
        deathWaveUnlocked,
        chainLightningUnlocked,
        chronoFieldUnlocked,
        goldenTowerUnlocked,
        innerLandMinesUnlocked,
        poisonSwampUnlocked,
        blackHoleUnlocked,
        spotlightUnlocked,
        standardResearch,
        tradeoffResearch,
        waveReductionResearch,
        perkOptionResearch,
        coinBonuses,
        coinsPerKill,
        coinsPerWave,
        goldenTowerBonus,
        goldenTowerDuration,
        goldenTowerCooldown,
        blackHoleCoverage,
        blackHoleCoins,
        blackHoleDuration,
        blackHoleCooldown,
        spotlightCoins,
        spotlightAngle,
        spotlightQuantity,
        criticalCoin,
        firstPerkChoice,
        bannedPerks,
        perkPriorityList,
        neverPickPerks,
        gameSpeed,
        waveSkip,
        introSprint,
        autopick,
        waveAccelerator,
        verbose,
      }),
    [
      smartMissilesUnlocked,
      maxWave,
      deathWaveUnlocked,
      chainLightningUnlocked,
      chronoFieldUnlocked,
      goldenTowerUnlocked,
      innerLandMinesUnlocked,
      poisonSwampUnlocked,
      blackHoleUnlocked,
      spotlightUnlocked,
      standardResearch,
      tradeoffResearch,
      waveReductionResearch,
      perkOptionResearch,
      coinBonuses,
      coinsPerKill,
      coinsPerWave,
      goldenTowerBonus,
      goldenTowerDuration,
      goldenTowerCooldown,
      blackHoleCoverage,
      blackHoleCoins,
      blackHoleDuration,
      blackHoleCooldown,
      spotlightCoins,
      spotlightAngle,
      spotlightQuantity,
      criticalCoin,
      firstPerkChoice,
      bannedPerks,
      perkPriorityList,
      neverPickPerks,
      gameSpeed,
      waveSkip,
      introSprint,
      autopick,
      waveAccelerator,
    ],
  );

  const runSimulation = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const { stats, log } = simulation(true);
      setResults(oldResults => {
        const newResults = addResults(cloneDeep(oldResults), stats);
        return newResults;
      });
      setLog(log);
    },
    [setLog, setResults, simulation],
  );

  const runMany = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      setLog([]);
      setRunning(true);
      setProgress(0);
      const simulationResults: ResultsType = getInitialResults();
      let currentCount = 0;
      const idleCallback = deadline => {
        while (deadline.timeRemaining() && currentCount < simulationCount) {
          currentCount += 1;
          const { stats } = simulation(false);
          addResults(simulationResults, stats);
        }
        if (currentCount < simulationCount) {
          setProgress(currentCount);
          cancelRef.current = requestIdleCallback(idleCallback);
        } else {
          setResults(oldResults => mergeResults(oldResults, simulationResults));
          setProgress(currentCount);
          setRunning(false);
          cancelRef.current = undefined;
        }
      };
      cancelRef.current = requestIdleCallback(idleCallback);
    },
    [setLog, simulationCount, simulation, setResults],
  );

  useEffect(() => {
    const cleanup = () => {
      if (cancelRef.current) cancelIdleCallback(cancelRef.current);
    };
    return cleanup;
  }, []);

  const progressPercent = `${((progress * 100) / simulationCount).toFixed(2)}%`;

  return (
    <div id='simulator'>
      <div id='controls'>
        {running ? (
          <div className='progress'>
            <div className='progress-bar' style={{ width: progressPercent }} />
            <div className='text'>
              {progress} / {simulationCount} (
              {((progress * 100) / simulationCount).toFixed(2)}%)
            </div>
          </div>
        ) : (
          <>
            <button type='button' onClick={runSimulation}>
              Simulate Once w/ Log
            </button>
            <button type='button' onClick={runMany}>
              Simulate {simulationCount}
            </button>
            <button type='button' onClick={clear}>
              Clear Results
            </button>
          </>
        )}
        {!running && (
          <div className='view-selector'>
            <select value={view} onChange={setView}>
              <option value={Views.TABLE}>Table</option>
              <option value={Views.AVERAGES}>Averages</option>
              <option value={Views.GRAPHS}>Graphs</option>
              <option value={Views.LOG}>Log</option>
              <option value={Views.CSV}>CSV</option>
            </select>
          </div>
        )}
      </div>
      {!running && (
        <div className='results'>
          {view === Views.AVERAGES && <Averages />}
          {view === Views.TABLE && <ResultsTable />}
          {view === Views.GRAPHS && <Graphs />}
          {view === Views.CSV && <CsvTables />}
          {view === Views.LOG && <Log />}
        </div>
      )}
    </div>
  );
};

export default Options;
